import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Edit2,
  UserX,
  UserCheck,
  Shield,
  Mail,
  Phone,
  Calendar,
  MoreVertical,
  Key,
  Trash2,
  Users,
  UserCog,
  Building
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';

const ROLES = [
  { id: 'super_admin', label: 'Super Admin', description: 'Full system access', color: 'text-red-400', bg: 'bg-red-500/20' },
  { id: 'admin', label: 'Admin', description: 'CMS, users, settings access', color: 'text-orange-400', bg: 'bg-orange-500/20' },
  { id: 'manager', label: 'Manager', description: 'Requests, quotes, clients, projects', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  { id: 'editor', label: 'Editor', description: 'CMS and content editing', color: 'text-green-400', bg: 'bg-green-500/20' },
  { id: 'finance', label: 'Finance', description: 'Quotes and reports', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  { id: 'team', label: 'Team Member', description: 'Requests and projects', color: 'text-purple-400', bg: 'bg-purple-500/20' },
  { id: 'client', label: 'Client', description: 'Client portal access', color: 'text-cyan-400', bg: 'bg-cyan-500/20' },
];

const UsersPage = () => {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'team',
    client_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, clientsRes] = await Promise.all([
        api.get('/users'),
        api.get('/clients')
      ]);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      role: 'team',
      client_id: ''
    });
    setDialogOpen(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      password: '',
      role: user.role,
      client_id: user.client_id || ''
    });
    setDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!formData.name || !formData.email || !formData.role) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error('Password is required for new users');
      return;
    }

    try {
      if (editingUser) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          role: formData.role,
        };
        if (formData.role === 'client' && formData.client_id) {
          updateData.client_id = formData.client_id;
        }
        await api.patch(`/users/${editingUser.id}`, updateData);
        toast.success('User updated successfully');
      } else {
        const createData = { ...formData };
        if (formData.role !== 'client') {
          delete createData.client_id;
        }
        await api.post('/users', createData);
        toast.success('User created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.patch(`/users/${user.id}`, { active: !user.active });
      toast.success(user.active ? 'User deactivated' : 'User activated');
      fetchData();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await api.patch(`/users/${editingUser.id}`, { password: newPassword });
      toast.success('Password reset successfully');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setEditingUser(null);
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error('Failed to reset password');
    }
  };

  const openPasswordDialog = (user) => {
    setEditingUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const getRoleInfo = (roleId) => {
    return ROLES.find(r => r.id === roleId) || { label: roleId, color: 'text-gray-400', bg: 'bg-gray-500/20' };
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || 'No company';
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && user.active) ||
      (statusFilter === 'inactive' && !user.active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Stats
  const stats = {
    total: users.length,
    active: users.filter(u => u.active).length,
    admins: users.filter(u => ['super_admin', 'admin', 'manager'].includes(u.role)).length,
    clients: users.filter(u => u.role === 'client').length,
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-fp-surface rounded-xl border border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-fp-textMuted text-xs">Total Users</p>
                <p className="text-xl font-bold text-white">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-fp-surface rounded-xl border border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-fp-textMuted text-xs">Active</p>
                <p className="text-xl font-bold text-white">{stats.active}</p>
              </div>
            </div>
          </div>
          <div className="bg-fp-surface rounded-xl border border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-fp-textMuted text-xs">Team</p>
                <p className="text-xl font-bold text-white">{stats.admins}</p>
              </div>
            </div>
          </div>
          <div className="bg-fp-surface rounded-xl border border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <Building className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-fp-textMuted text-xs">Clients</p>
                <p className="text-xl font-bold text-white">{stats.clients}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="users-page-title">User Management</h1>
            <p className="text-fp-textMuted text-sm mt-1">Manage team members and client accounts</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fp-textMuted" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-fp-surface border-white/10 text-white w-64"
                data-testid="users-search"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-36 bg-fp-surface border-white/10 text-white">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent className="bg-fp-surface border-white/10">
                <SelectItem value="all">All Roles</SelectItem>
                {ROLES.map(role => (
                  <SelectItem key={role.id} value={role.id}>{role.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32 bg-fp-surface border-white/10 text-white">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="bg-fp-surface border-white/10">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleCreateUser} className="btn-primary" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-fp-surface rounded-xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-6 py-4 text-fp-textMuted text-sm font-medium">User</th>
                  <th className="text-left px-6 py-4 text-fp-textMuted text-sm font-medium">Role</th>
                  <th className="text-left px-6 py-4 text-fp-textMuted text-sm font-medium">Contact</th>
                  <th className="text-left px-6 py-4 text-fp-textMuted text-sm font-medium">Status</th>
                  <th className="text-left px-6 py-4 text-fp-textMuted text-sm font-medium">Created</th>
                  <th className="text-left px-6 py-4 text-fp-textMuted text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-fp-textMuted">
                      Loading...
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-fp-textMuted">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => {
                    const roleInfo = getRoleInfo(user.role);
                    const isCurrentUser = user.id === currentUser?.id;
                    
                    return (
                      <motion.tr
                        key={user.id}
                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        data-testid={`user-row-${user.id}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                              <span className="text-primary font-medium">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-white font-medium flex items-center gap-2">
                                {user.name}
                                {isCurrentUser && (
                                  <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded">You</span>
                                )}
                              </p>
                              <p className="text-fp-textMuted text-sm">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-sm ${roleInfo.bg} ${roleInfo.color}`}>
                            {roleInfo.label}
                          </span>
                          {user.role === 'client' && user.client_id && (
                            <p className="text-fp-textMuted text-xs mt-1">{getClientName(user.client_id)}</p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {user.phone ? (
                            <div className="flex items-center gap-1 text-fp-textMuted text-sm">
                              <Phone className="w-3 h-3" />
                              {user.phone}
                            </div>
                          ) : (
                            <span className="text-fp-textMuted text-sm">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {user.active ? (
                            <span className="flex items-center gap-1 text-green-400 text-sm">
                              <div className="w-2 h-2 rounded-full bg-green-400" />
                              Active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-400 text-sm">
                              <div className="w-2 h-2 rounded-full bg-red-400" />
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-fp-textMuted text-sm">{formatDate(user.created_at)}</p>
                        </td>
                        <td className="px-6 py-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-2 hover:bg-white/10 rounded" data-testid={`user-actions-${user.id}`}>
                                <MoreVertical className="w-4 h-4 text-fp-textMuted" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-fp-surface border-white/10">
                              <DropdownMenuItem onClick={() => handleEditUser(user)}>
                                <Edit2 className="w-4 h-4 mr-2" />
                                Edit User
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openPasswordDialog(user)}>
                                <Key className="w-4 h-4 mr-2" />
                                Reset Password
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-white/10" />
                              {!isCurrentUser && (
                                <DropdownMenuItem 
                                  onClick={() => handleToggleActive(user)}
                                  className={user.active ? 'text-red-400' : 'text-green-400'}
                                >
                                  {user.active ? (
                                    <>
                                      <UserX className="w-4 h-4 mr-2" />
                                      Deactivate
                                    </>
                                  ) : (
                                    <>
                                      <UserCheck className="w-4 h-4 mr-2" />
                                      Activate
                                    </>
                                  )}
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create/Edit User Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-fp-surface border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingUser ? 'Edit User' : 'Add New User'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-fp-textMuted">Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                  data-testid="user-name-input"
                />
              </div>
              
              <div>
                <Label className="text-fp-textMuted">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                  data-testid="user-email-input"
                />
              </div>
              
              <div>
                <Label className="text-fp-textMuted">Phone</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                />
              </div>
              
              {!editingUser && (
                <div>
                  <Label className="text-fp-textMuted">Password *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Minimum 6 characters"
                    className="mt-1 bg-fp-bg border-white/10 text-white"
                    data-testid="user-password-input"
                  />
                </div>
              )}
              
              <div>
                <Label className="text-fp-textMuted">Role *</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger className="mt-1 bg-fp-bg border-white/10 text-white" data-testid="user-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="bg-fp-surface border-white/10">
                    {ROLES.map(role => (
                      <SelectItem key={role.id} value={role.id}>
                        <div className="flex items-center gap-2">
                          <span className={role.color}>{role.label}</span>
                          <span className="text-fp-textMuted text-xs">- {role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {formData.role === 'client' && (
                <div>
                  <Label className="text-fp-textMuted">Link to Company</Label>
                  <Select 
                    value={formData.client_id} 
                    onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                  >
                    <SelectTrigger className="mt-1 bg-fp-bg border-white/10 text-white">
                      <SelectValue placeholder="Select company (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-fp-surface border-white/10">
                      <SelectItem value="">No company</SelectItem>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="flex gap-3 justify-end pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">
                  Cancel
                </Button>
                <Button onClick={handleSaveUser} className="btn-primary" data-testid="save-user-btn">
                  {editingUser ? 'Save Changes' : 'Create User'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent className="bg-fp-surface border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Reset Password</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-fp-textMuted text-sm">
                Set a new password for <span className="text-white font-medium">{editingUser?.name}</span>
              </p>
              
              <div>
                <Label className="text-fp-textMuted">New Password *</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                  data-testid="new-password-input"
                />
              </div>
              
              <div className="flex gap-3 justify-end pt-4">
                <Button variant="outline" onClick={() => setPasswordDialogOpen(false)} className="border-white/10">
                  Cancel
                </Button>
                <Button onClick={handleResetPassword} className="btn-primary" data-testid="reset-password-btn">
                  Reset Password
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default UsersPage;
